import { c as _c } from "react/compiler-runtime";
import { Temporal } from "@js-temporal/polyfill";
import { Clock, ListChecks } from "lucide-react";
import { useTranslations } from "next-intl";
import { Card } from "@/components/ui/card";
import { formatDuration } from "@/lib/time";
import { cn } from "@/lib/utils";
interface UserStatsSummaryProps {
  /** Total helper time in seconds. */
  seconds: number;
  /** Number of tasks. */
  taskCount: number;
  /**
   * `"card"` renders large stat cards (dashboard/profile); `"inline"` renders a
   * compact one-line summary (user-icon popover).
   */
  variant?: "card" | "inline";
  className?: string;
}
export function UserStatsSummary(t0) {
  const $ = _c(43);
  const {
    seconds,
    taskCount,
    variant: t1,
    className
  } = t0;
  const variant = t1 === undefined ? "card" : t1;
  const t = useTranslations();
  let t2;
  if ($[0] !== seconds) {
    t2 = formatDuration(Temporal.Duration.from({
      seconds
    }));
    $[0] = seconds;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const duration = t2;
  const helperTime = duration || "0min";
  if (variant === "inline") {
    let t3;
    if ($[2] !== className) {
      t3 = cn("flex items-center gap-3 text-xs text-muted-foreground", className);
      $[2] = className;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Clock className="h-3 w-3" />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== helperTime) {
      t5 = <span className="flex items-center gap-1">{t4}{helperTime}</span>;
      $[5] = helperTime;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <ListChecks className="h-3 w-3" />;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t || $[9] !== taskCount) {
      t7 = t("stats.task-count-value", {
        count: taskCount
      });
      $[8] = t;
      $[9] = taskCount;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t7) {
      t8 = <span className="flex items-center gap-1">{t6}{t7}</span>;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== t3 || $[14] !== t5 || $[15] !== t8) {
      t9 = <div className={t3}>{t5}{t8}</div>;
      $[13] = t3;
      $[14] = t5;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  let t3;
  if ($[17] !== className) {
    t3 = cn("grid grid-cols-2 gap-3", className);
    $[17] = className;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Clock className="h-5 w-5 shrink-0 text-muted-foreground" />;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== helperTime) {
    t5 = <div className="text-lg font-semibold leading-none">{helperTime}</div>;
    $[20] = helperTime;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t) {
    t6 = t("stats.helper-time");
    $[22] = t;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t6) {
    t7 = <div className="mt-1 text-sm text-muted-foreground">{t6}</div>;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t5 || $[27] !== t7) {
    t8 = <Card className="flex flex-row items-center gap-3 p-3">{t4}<div className="min-w-0">{t5}{t7}</div></Card>;
    $[26] = t5;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ListChecks className="h-5 w-5 shrink-0 text-muted-foreground" />;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== taskCount) {
    t10 = <div className="text-lg font-semibold leading-none">{taskCount}</div>;
    $[30] = taskCount;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t) {
    t11 = t("stats.task-count");
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t11) {
    t12 = <div className="mt-1 text-sm text-muted-foreground">{t11}</div>;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t10 || $[37] !== t12) {
    t13 = <Card className="flex flex-row items-center gap-3 p-3">{t9}<div className="min-w-0">{t10}{t12}</div></Card>;
    $[36] = t10;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13 || $[40] !== t3 || $[41] !== t8) {
    t14 = <div className={t3}>{t8}{t13}</div>;
    $[39] = t13;
    $[40] = t3;
    $[41] = t8;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}